export interface GocardlessConnection {
  id: string;
  company_id: number;
  organisation_id: string | null;
  environment: string;
  status: string;
  connected_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OAuthInitiateResponse {
  url: string;
}

export interface OAuthExchangeRequest {
  code: string;
  state: string;
}

export type MandateStatus = 'active' | 'pending' | 'none';

/**
 * Lookup-style options for the MandateStatus enum, suitable for multi-select filters
 * or dropdowns. Shared across the client-signups table search bar and the bulk-send
 * wizard.
 */
export const MANDATE_STATUS_OPTIONS: Array<{ id: MandateStatus; name: string }> = [
  { id: 'active', name: 'Active' },
  { id: 'pending', name: 'Pending' },
  { id: 'none', name: 'None' },
];

export interface GocardlessClientSignupRow {
  client_id: number;
  first_name: string | null;
  surname: string | null;
  full_name: string | null;
  email: string | null;
  status: MandateStatus;
  has_outstanding_invoices: boolean;
  outstanding_invoice_count: number;
  last_sent_at: string | null;
}

export interface GocardlessSignupStats {
  total_clients: number;
  active_mandates: number;
  pending_mandates: number;
  no_mandate: number;
}

export interface GocardlessClientSignupsResponse {
  rows: GocardlessClientSignupRow[];
  total: number;
  stats: GocardlessSignupStats;
}

export interface BulkSignupFilters {
  mandate_states: ('no_mandate' | 'pending')[];
  has_outstanding_invoice?: boolean;
  has_email?: boolean;
  last_sent_days?: number;
}

// Form-local mandate filter. `true` = "All Users" (include clients with no
// mandate AND with a pending mandate). `false` = "No Mandates" (clients with
// no mandate only). Active-mandate clients are always hard-excluded
// server-side, so "All Users" means "everyone the wizard is allowed to target".
export type MandateFilter = boolean;

export const resolveMandateStates = (allUsers: MandateFilter): BulkSignupFilters['mandate_states'] =>
  allUsers ? ['no_mandate', 'pending'] : ['no_mandate'];

export interface BulkSignupRecipient {
  id: number;
  first_name: string | null;
  surname: string | null;
  email: string;
}

export interface BulkSignupCountResponse {
  count: number;
  recipients?: BulkSignupRecipient[];
}

export interface BulkSignupResult {
  client_id: number;
  status: 'sent' | 'failed';
  stage?: 'gocardless' | 'email';
  message?: string;
}

export interface BulkSignupResponse {
  sent_count: number;
  failed_count: number;
  results: BulkSignupResult[];
}

// ---------------------------------------------------------------------------
// Mandate Matching (TAV-2049)
// ---------------------------------------------------------------------------

export interface GocardlessMandateStatus {
  tag: string;
  name: string;
  /** Hex colour without the leading '#', e.g. "4CAF50". The badge prefixes '#'. */
  colour: string;
}

export interface GocardlessMandateMatch {
  id: string;
  client_id: number;
  client_name: string | null;
}

export interface GocardlessMandate {
  id: string;
  // TurnPoint internal identifier (DDM-NNNNNN), auto-generated, always present.
  reference: string;
  gocardless_mandate_id: string;
  gocardless_customer_id: string | null;
  customer_name: string | null;
  // Customer-set reference returned by GoCardless (often null).
  gocardless_reference: string | null;
  status: GocardlessMandateStatus;
  is_ignored: boolean;
  is_linked: boolean;
  match: GocardlessMandateMatch | null;
  created_at: string;
}

export interface GocardlessMandateListResponse {
  rows: GocardlessMandate[];
  total: number;
  // Inverse-view counter — when listing active, this is the ignored count, and
  // vice-versa. Powers the "X ignored" / "Y active" UI hint.
  counter: number;
}

export interface GocardlessMandateListQuery {
  is_ignored?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'created_at' | 'customer_name' | 'reference' | 'status';
  sortOrder?: 'asc' | 'desc';
  filter?: {
    customer_name?: string;
    // The new internal DDM-NNNNNN identifier.
    reference?: string;
    // The customer-set reference returned by GoCardless (was `reference` pre-TAV-2052).
    gocardless_reference?: string;
  };
}

// Mandate statuses considered non-linkable. Mirrored from the API service. The
// Link button in the matching UI is disabled when the mandate's status tag is
// in this set, and the API returns 400 if a client attempts the link anyway.
export const NON_LINKABLE_MANDATE_TAGS = new Set(['cancelled', 'expired', 'failed', 'consumed', 'blocked']);

// ---------------------------------------------------------------------------
// Payment Batches (TAV-2051)
// ---------------------------------------------------------------------------

/**
 * Generic lookup-style status payload shared by Payment Batches, Payments, and
 * Invoices. Mirrors the {tag, name, colour} shape supplied by the API for any
 * lookup-driven status. `colour` is a hex string with no leading hash.
 */
export interface PaymentLookupStatus {
  id?: string;
  name: string;
  tag: string;
  /** Hex colour without the leading '#', e.g. "4CAF50". The badge prefixes '#'. */
  colour: string;
}

export interface PaymentBatch {
  id: string;
  // Internal `DDB-NNNNNN` identifier — the user-facing one shown in the list
  // and detail header. The UUID `id` stays for routing/keys but is never
  // surfaced as visible text.
  reference: string;
  collection_date: string | null;
  description: string | null;
  total_amount: string;
  client_count: number;
  created_at: string;
  status: PaymentLookupStatus;
}

export interface PaymentBatchStats {
  total: number;
  completed: number;
  processing: number;
  failed: number;
  cancelled: number;
}

export interface PaymentBatchListResponse {
  rows: PaymentBatch[];
  total: number;
  stats: PaymentBatchStats;
}

/**
 * Summary of the refund raised against a payment line, surfaced on the batch
 * detail row so the line can show a "Refunded · DDR-…" chip and suppress the
 * Refund action. Present only once a refund exists (TAV-2833).
 */
export interface PaymentBatchClientRefund {
  // Internal `DDR-NNNNNN` refund reference.
  reference: string;
  status: PaymentLookupStatus;
}

export interface PaymentBatchClient {
  client_id: number;
  client_name: string;
  payment_status: PaymentLookupStatus | null;
  amount: string;
  invoice_count: number;
  // TAV-2833 — the batch-detail API supplies these so the row can drive the
  // per-payment Refund/Reverse controls. Optional until the API change ships;
  // the controls stay hidden until `payment_batch_item_id` is present.
  // The refund/reverse target is `gocardless_payment_id` (the line is 1-1 with
  // a GoCardless payment); `payment_batch_item_id` is what the mutations send.
  payment_batch_item_id?: string;
  // The line's `DDI-NNNNNN` reference, shown in the action modals.
  reference?: string;
  // GoCardless `PM…` id, shown in the modals for dashboard cross-reference.
  gocardless_payment_id?: string | null;
  // Populated once a refund exists against this line.
  refund?: PaymentBatchClientRefund | null;
  // Drive the batch-detail retry-alert icon and Next Charge Date columns.
  // `retry_scheduled` marks a failed line awaiting an automatic retry (still
  // `submitted`); `next_charge_date` is the upcoming attempt date, null for a
  // terminal line.
  retry_scheduled?: boolean;
  next_charge_date?: string | null;
}

export interface PaymentBatchDetail {
  id: string;
  reference: string;
  collection_date: string | null;
  description: string | null;
  total_amount: string;
  client_count: number;
  created_at: string;
  status: PaymentLookupStatus;
  clients: PaymentBatchClient[];
}

export interface PaymentBatchInvoice {
  id: string;
  reference: string;
  invoice_date: string;
  amount: string;
  status: PaymentLookupStatus;
}

export interface PaymentBatchInvoicesResponse {
  rows: PaymentBatchInvoice[];
}

// ---------------------------------------------------------------------------
// Refunds & Reversals (TAV-2833)
// ---------------------------------------------------------------------------

/** A refund row returned by `POST /gocardless/refunds`. */
export interface GocardlessRefund {
  id: string;
  // Internal `DDR-NNNNNN` reference.
  reference: string;
  payment_batch_item_id: string;
  gocardless_refund_id: string | null;
  amount: string;
  reason: string | null;
  status: PaymentLookupStatus;
}

/** Body for `POST /gocardless/refunds`. There are no partial refunds — the
 * full line amount is always refunded — so only the line + reason are sent. */
export interface RefundCreateRequest {
  payment_batch_item_id: string;
  reason: string;
}

/** Body for `POST /gocardless/reversals`. Reason is optional for reversals. */
export interface ReversalCreateRequest {
  payment_batch_item_id: string;
  reason?: string;
}

/** Result of `POST /gocardless/reversals` — the cancelled payment line. */
export interface ReversalResult {
  payment_batch_item_id: string;
  status: PaymentLookupStatus;
}

// ---------------------------------------------------------------------------
// Payment Batches Wizard (TAV-2052)
// ---------------------------------------------------------------------------

export interface PaymentBatchPreviewStats {
  client_count: number;
  invoice_count: number;
  total_amount: number;
}

export interface PaymentBatchPreviewInvoice {
  id: string;
  reference: string;
  invoice_date: string | null;
  due_date: string | null;
  amount: number;
}

export interface PaymentBatchPreviewClient {
  client_id: number;
  client_name: string | null;
  mandate_id: string;
  invoice_count: number;
  total_amount: number;
  invoices: PaymentBatchPreviewInvoice[];
}

export interface PaymentBatchPreviewFilters {
  due_date_cutoff: string | null;
  unpaid_only: true;
  // "Payable" rather than "active": GoCardless will not move a BECS-AU mandate
  // past `pending_submission` until a payment is created against it, so the
  // wizard's eligibility set covers `active`, `pending_submission`, and
  // `submitted`. The flag stays a literal `true` because it's a pure marker
  // documenting which filter the API applied.
  has_payable_mandate: true;
  not_in_existing_batch: true;
}

export interface PaymentBatchPreviewResponse {
  filters_applied: PaymentBatchPreviewFilters;
  stats: PaymentBatchPreviewStats;
  clients: PaymentBatchPreviewClient[];
}

export interface PaymentBatchPreviewQueryArgs {
  due_date_cutoff?: string;
}

export interface PaymentBatchCreateRequest {
  description?: string;
  due_date_cutoff?: string;
}
